import { useState, type FormEvent } from "react";
import { confirmForClient, formatDate, makeSafeT, toDateTimeLocal } from "./core";
import type { StaffPaneProps } from "./console-types";
import type { StaffRole, StaffRun } from "./model";
import { callCodeStateKey } from "./PhaseCards";
export function CallsPane(props: StaffPaneProps) {
  const [lastCode, setLastCode] = useState<{ callId: string; code: string } | null>(null);
  async function issueCode(callId: string) {
    await issueOneTimeCode(props.run, callId, (code) => setLastCode({ callId, code }));
  }
  return <div className="staff-card-list">{lastCode && <p className="one-time-code">{props.t("staffConsole.oneTimeCode")} <code>{lastCode.code}</code></p>}{props.detail.calls.length ? props.detail.calls.map((call) => (
    <article className="staff-card" key={String(call.id)}>
      <div className="card-row"><strong>{makeSafeT(props.t)(`staffConsole.callKindValue.${String(call.kind)}`, String(call.kind))}</strong><span>{formatDate(String(call.startsAt), props.locale)}</span></div>
      <p>{call.status ? makeSafeT(props.t)(`staffConsole.enrollment.${String(call.status)}`, String(call.status)) : props.t("staffConsole.clientNotEnrolled")}</p>
      {/* Facts row: link and code STATUS with real spacing. The raw
          unlock_codes UUID never renders (partner reads words, N20). */}
      <div className="call-facts-row">
        {typeof call.zoomUrl === "string" && <a className="quiet-link" href={call.zoomUrl} target="_blank" rel="noreferrer">{props.t("staffConsole.openZoom")}</a>}
        <span className="call-code-state">{props.t(callCodeStateKey(call))}</span>
      </div>
      {props.role === "admin" && !call.unlockCodeId && lastCode?.callId !== String(call.id) && <button className="secondary-button compact-button" type="button" onClick={() => void issueCode(String(call.id))}>{props.t("staffConsole.issueCode")}</button>}
      {props.role === "admin" && <CallAdminActions call={call} {...props} />}
    </article>
  )) : <p className="staff-empty">{props.t("staffConsole.emptyCalls")}</p>}</div>;
}

export function CallAdminActions(props: StaffPaneProps & { call: Record<string, unknown> }) {
  const actions = callActionAvailability(props.call);
  const [startsAt, setStartsAt] = useState(toDateTimeLocal(String(props.call.startsAt ?? "")));
  const [locale, setLocale] = useState(String(props.call.locale ?? props.locale));
  const [zoomUrl, setZoomUrl] = useState(String(props.call.zoomUrl ?? ""));
  const [recordingUrl, setRecordingUrl] = useState(String(props.call.recordingUrl ?? ""));
  const [enrollmentStatus, setEnrollmentStatus] = useState(String(props.call.status ?? "enrolled"));
  async function save(event: FormEvent) {
    event.preventDefault();
    await props.run(`/api/admin/calls/${String(props.call.id)}`, {
      method: "PATCH",
      body: { startsAt: new Date(startsAt).toISOString(), locale, zoomUrl, recordingUrl: recordingUrl || null }
    });
  }
  async function saveEnrollment() {
    await props.run(`/api/admin/clients/${props.detail.client.id}/calls/${String(props.call.id)}/enrollment`, {
      method: "PUT",
      body: { status: enrollmentStatus }
    });
  }
  async function removeCall() {
    if (!confirmForClient(props, "staffConsole.confirmCallDelete")) return;
    await props.run(`/api/admin/calls/${String(props.call.id)}`, { method: "DELETE" });
  }
  async function revokeCode() {
    if (!confirmForClient(props, "staffConsole.confirmCodeRevoke")) return;
    await props.run(`/api/admin/codes/${String(props.call.unlockCodeId)}/revoke`, { method: "POST", body: {} });
  }
  return <form className="editor-form call-editor" onSubmit={save}>
    <input type="datetime-local" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} aria-label={props.t("staffConsole.callStartsAt")} required />
    <select value={locale} onChange={(event) => setLocale(event.target.value)} aria-label={props.t("staffConsole.callLocale")}><option value="cs">{props.t("common.languageCs")}</option><option value="en">{props.t("common.languageEn")}</option></select>
    <input type="url" value={zoomUrl} onChange={(event) => setZoomUrl(event.target.value)} aria-label={props.t("staffConsole.zoomUrl")} />
    <input type="url" value={recordingUrl} onChange={(event) => setRecordingUrl(event.target.value)} aria-label={props.t("staffConsole.recordingUrl")} />
    <div className="inline-fields">
      <select value={enrollmentStatus} onChange={(event) => setEnrollmentStatus(event.target.value)} aria-label={props.t("staffConsole.enrollmentStatus")}><option value="enrolled">{props.t("staffConsole.enrollment.enrolled")}</option><option value="attended">{props.t("staffConsole.enrollment.attended")}</option><option value="watched_recording">{props.t("staffConsole.enrollment.watchedRecording")}</option></select>
      <button className="secondary-button" type="button" onClick={() => void saveEnrollment()}>{props.call.status ? props.t("staffConsole.saveEnrollment") : props.t("staffConsole.enrollClient")}</button>
    </div>
    <div className="button-row"><button className="secondary-button" type="submit">{props.t("staffConsole.saveCall")}</button>{actions.canRevoke && <button className="text-button danger" type="button" onClick={() => void revokeCode()}>{props.t("staffConsole.revokeCode")}</button>}{actions.canDelete && <button className="text-button danger" type="button" onClick={() => void removeCall()}>{props.t("staffConsole.deleteCall")}</button>}</div>
  </form>;
}

export async function issueOneTimeCode(
  run: StaffRun,
  callId: string,
  reveal: (code: string) => void,
  now = Date.now()
): Promise<Response> {
  const response = await run(`/api/admin/calls/${callId}/code`, {
    method: "POST",
    body: { notBefore: new Date(now).toISOString(), expiresAt: new Date(now + 7 * 86_400_000).toISOString() }
  }, { refresh: false });
  if (response.ok) {
    const body = await response.json() as { code?: string };
    if (body.code) reveal(body.code);
  }
  return response;
}

export function callActionAvailability(call: Record<string, unknown>): { canDelete: boolean; canRevoke: boolean } {
  return {
    canDelete: call.hasEnrollments !== 1,
    canRevoke: Boolean(call.unlockCodeId) && call.codeActive === 1 && call.hasRedemptions !== 1
  };
}

export function deliverableActionAvailability(
  item: { status: string; published: boolean },
  role: StaffRole
): { editable: boolean; canPublish: boolean; canDelete: boolean } {
  const editable = role === "admin" && item.status !== "approved";
  return {
    editable,
    canPublish: editable && !item.published,
    canDelete: editable && !item.published && item.status === "draft"
  };
}
